import { HttpClient, HttpResponse } from '@angular/common/http'
import { ParamMap } from '@angular/router'
import { Observable } from 'rxjs'

import { FilterMetadata } from 'primeng/api'
import { TableLazyLoadEvent } from 'primeng/table'
import { environment } from 'environments/environment'
import { saveAs } from 'file-saver-es'
import { isBoolean } from 'lodash-es'
import { DateTime } from 'luxon'

import type { LongRunningJob } from '@app/generated/api/model'

import { PagedResponse, StaticDropDown } from './generic'

export type Params = { [key: string]: string | number | boolean }

/**
 * extract filtering and sort params from the primeng LazyLoad event ready for
 * django / DRF filtering
 *
 * example LazyLoadEvent content:
 *  // filters: {value: 'xx', matchMode='startsWith'}
 *  // first: 0
 *  // globalFilter: null
 *  // multiSortMeta: undefined
 *  // rows: 10
 */
export function lazyLoadEventToUrlParams (event?: TableLazyLoadEvent): Params {
  const cleanFilters: Params = {}

  if (!event) return cleanFilters

  cleanFilters.limit = event.rows ?? 100
  cleanFilters.offset = event.first ?? 0

  for (const key in event.filters ?? {}) {
    if (event.filters![key]) {
      const filters = asArray(event.filters![key])

      // loop over all the filters in the event and build the django-friendly filter clauses
      filters.forEach(filter => {
        if (filter.value === null) return
        const [filterKey, filterValue] = buildFilterKeyValue(filter, key)
        cleanFilters[filterKey] = filterValue
      })
    }
  }

  // build multi-field sort
  const orderFields: string[] = []
  event.multiSortMeta?.forEach(sortData => {
    let sortField = sortData.field
    if (sortData.order === -1) {
      sortField = '-' + sortField
    }
    orderFields.push(sortField)
  })
  cleanFilters.ordering = orderFields.join(',')

  return cleanFilters
}

export function buildFilterKeyValue (filter: FilterMetadata, key: string): [string, any] {
  if (filter.matchMode === 'equals' && filter.value === 'true') filter.value = true
  if (filter.matchMode === 'equals' && filter.value === 'false') filter.value = false

  switch (filter.matchMode) {
    case 'notExists':
    case 'exists': {
      // Exists is passed to django as `__isnull`, so the boolean value should be inverted
      let value = isBoolean(filter.value) ? !filter.value : filter.value
      if (filter.matchMode === 'notExists') value = !value
      return [key + '__isnull', value]
    }
    case 'in': // `__in` clauses should be passed to django as a comma-separated list from the original array
      return [key + '__in', filter.value.join(',')]
    case 'contains':
      return [key + '__contains', filter.value]
    case 'notContains':
      return [key + '__ncontains', filter.value]
    case 'startsWith':
      return [key + '__startswith', filter.value]
    case 'endsWith':
      return [key + '__endswith', filter.value]
    case 'notEquals':
      return [key + '__neq', filter.value]
    case 'lt':
      return [key + '__lt', filter.value]
    case 'lte':
      return [key + '__lte', filter.value]
    case 'gt':
      return [key + '__gt', filter.value]
    case 'gte':
      return [key + '__gte', filter.value]
    // date matchModes are constructed to match the `DateFiltersMixin`, supporting `__date_exact`, `__before` and `__after`
    case 'dateIs': { // convert date object to consistent string representation
      const value = filter.value instanceof Date ? DateTime.fromJSDate(filter.value).toISO() : filter.value
      return [key + '__date_exact', value]
    }
    case 'dateBefore': {
      const value = filter.value instanceof Date ? DateTime.fromJSDate(filter.value).toISO() : filter.value
      return [key + '__before', value]
    }
    case 'dateAfter': {
      const value = filter.value instanceof Date ? DateTime.fromJSDate(filter.value).toISO() : filter.value
      return [key + '__after', value]
    }
    case 'equals':
    default: { // fallback to simple fieldname=value, but still always forcing dates to a string
      return [key, filter.value]
    }
  }
}

/**
 * convert the URLParams present in the URL to a LazyLoadEvent suitable for initializing the pTable with, in order to persist
 * pTable filters via the URL
 */
export function urlParamsToLazyLoadEvent (params: Map<string, any>): TableLazyLoadEvent {
  const theEvent = createLazyLoadEvent(params)

  const sortFields: string = params.get('ordering') || ''
  sortFields.split(',').forEach(key => {
    if (!key) return
    let order = 1
    if (key.startsWith('-')) {
      order = -1
      key = key.slice(1, key.length)
    }
    theEvent.multiSortMeta!.push({
      field: key,
      order,
    })
  })

  params.forEach((paramValue, paramKey) => {
    // ignore sort and pagination keys
    if (['offset', 'limit', 'ordering'].includes(paramKey)) return
    const { fieldName, matchMode, value } = createFilter(paramKey, paramValue)
    // now we have the prepared value, matchMode and field name ready for the pTable, init and append to the list of filters for this field.
    theEvent.filters = theEvent.filters ?? {}
    theEvent.filters[fieldName] = asArray(theEvent.filters[fieldName]);
    (theEvent.filters[fieldName]).push({
      value,
      matchMode,
      operator: 'and',
    })
  })

  return theEvent
}

function createLazyLoadEvent (params: Map<string, any>): TableLazyLoadEvent {
  return {
    first: parseInt(params.get('offset') || '0'),
    rows: parseInt(params.get('limit') || '100'),
    multiSortMeta: [],
    filters: {},
  }
}

function createFilter (key: string, value: any): { fieldName: string, matchMode: string, value: any } {
  const re = /.*(?<operator>__.*)/
  const matchResults = re.exec(key)
  let matchMode = 'equals'
  if (!(matchResults?.groups?.operator)) {
    return { fieldName: key, matchMode, value }
  }

  // for the magic (string) values of "true" and "false" we cast to boolean, to allow
  // selecting the correct (e.g. YESNO) option in the select when loading the filters from url.
  if (value === 'false') {
    value = false
  } else if (value === 'true') {
    value = true
  }
  const operator = matchResults.groups?.operator
  const fieldName = key.replace(operator, '')
  switch (operator) {
    case '__date_exact':
      matchMode = 'dateIs'
      // convert full ISO datetime to date
      value = DateTime.fromISO(value).toFormat('yyyy-MM-dd')
      return { fieldName, matchMode, value }
    case '__before':
      matchMode = 'dateBefore'
      return { fieldName, matchMode, value }
    case '__after':
      matchMode = 'dateAfter'
      return { fieldName, matchMode, value }
    case '__isnull':
      matchMode = 'exists'
      return { fieldName, matchMode, value: !value }
    case '__in':
      matchMode = 'in'
      // `in` matchModes imply that the value is in fact a list, which can potentially be a list of (foreign key int) ids.
      // Sadly there is no way to know if they should be int or strings from the incoming url params, so we make a guess that if they *can*
      // be integers then they probably should be cast as such.
      value = (value?.split(',') || []).map((e: any) => isNaN(parseInt(e)) ? e : parseInt(e))
      return { fieldName, matchMode, value }
    case '__contains':
      matchMode = 'contains'
      return { fieldName, matchMode, value }
    case '__ncontains':
      matchMode = 'notContains'
      return { fieldName, matchMode, value }
    case '__startswith':
      matchMode = 'startsWith'
      return { fieldName, matchMode, value }
    case '__endswith':
      matchMode = 'endsWith'
      return { fieldName, matchMode, value }
    case '__neq':
      matchMode = 'notEquals'
      return { fieldName, matchMode, value }
    case '__lt':
      matchMode = 'lt'
      return { fieldName, matchMode, value }
    case '__lte':
      matchMode = 'lte'
      return { fieldName, matchMode, value }
    case '__gt':
      matchMode = 'gt'
      return { fieldName, matchMode, value }
    case '__gte':
      matchMode = 'gte'
      return { fieldName, matchMode, value }
    default:
      return { fieldName: key, matchMode, value }
  }
}

/**
 * convert a ParamMap to a standard Map object, as a common denominator with URLSearchParams
 */
export function paramMapToMap (paramMap: ParamMap): Map<string, any> {
  const map = new Map<string, any>()
  if (!paramMap) return map

  for (const key of paramMap.keys) {
    map.set(key, paramMap.get(key))
  }
  return map
}

/**
 * convert a URLSearchParams to a standard Map object, as a common denominator with ParamMap
 */
export function urlSearchParamsToMap (urlSearchParams: URLSearchParams): Map<string, any> {
  const map = new Map<string, any>()

  for (const key of urlSearchParams.keys()) {
    map.set(key, urlSearchParams.get(key))
  }
  return map
}

export function cleanMapForPrefix (map: Map<string, any>, prefix: string) {
  const newMap: Map<string, any> = new Map<string, any>()
  for (const oldKey of map.keys()) {
    if (oldKey.indexOf(`${prefix}___`) > -1) {
      const newKey = oldKey.replace(`${prefix}___`, '')
      newMap.set(newKey, map.get(oldKey))
    } else {
      newMap.set(oldKey, map.get(oldKey))
    }
  }
  return newMap
}

/**
 *
 * Trigger a file download from an incoming blob payload.
 * Note that some presumptions are made regarding the presence of Content-type headers.
 *
 */
export function launchDownload (response: HttpResponse<Blob>): boolean {
  // try to determine the filename from the response headers
  let filename = 'unknown'
  const contentDisposition = response.headers.get('content-disposition')
  if (contentDisposition && contentDisposition.indexOf('attachment') !== -1) {
    const filenameRegex = /filename\*[^;=\n]*=UTF-8''((.*$)|[^;\n]*)/i
    const matches = filenameRegex.exec(contentDisposition)
    if (matches?.[1]) {
      filename = decodeURI(matches[1].replace(/['"]/g, ''))
    }
  }
  const file = new File([response.body!], filename, { type: response.headers.get('content-type') ?? 'application/octet-stream' })
  saveAs(file, filename)
  // returning true here allows the test to know we got to the end
  return true
}

/**
 * Clear the ServiceWorker API cache
 */
export function clearCache () {
  if ('caches' in window) {
    caches.keys().then((keys: string[]) => {
      return Promise.all(keys.map((key: string) => {
        return caches.delete(key)
      }))
    })
  }
}

/**
 * Retrieve a function to alphabetically compare the labels of 2 StaticDropDown.
 *
 * @param labelField Name of the field containing the label ('label_en' or 'label_fr').
 * @param locale Locale to be used for the comparison.
 * @returns function
 */
export function compareStaticDropDownLabelFunction (labelField: string, locale: string) {
  const collator = new Intl.Collator(locale)
  return (e1: StaticDropDown, e2: StaticDropDown) => {
    return collator.compare(e1[labelField], e2[labelField])
  }
}

export abstract class BaseApi {
  protected abstract get http (): HttpClient
  abstract get baseUrl (): string
}

export abstract class BaseApiV2<T> extends BaseApi {
  abstract get codeField (): keyof T // The name of the field containing the 'detail' code.
}

export interface ListableApi<T> {
  list (params: Params): Observable<PagedResponse<T>>
}

export abstract class ListableApi<T> extends BaseApiV2<T> {
  list (params: Params) {
    return this.http.get<PagedResponse<T>>(`${environment.apiRoot}/api/${this.baseUrl}/`, { params })
  }
}

export function isListableApi (object: any): object is ListableApi<any> {
  return 'create' in object && typeof object.create === 'function'
}

export interface CreatableApi<T> {
  create (t: T): Observable<T>
}

export abstract class CreatableApi<T> extends BaseApiV2<T> {
  create (t: T) {
    return this.http.post<T>(`${environment.apiRoot}/api/${this.baseUrl}/`, t)
  }
}

export function isCreatableApi (object: any): object is CreatableApi<any> {
  return 'create' in object && typeof object.create === 'function'
}

export interface RetrievableApi<T> {
  retrieve (code: string | number, params?: any): Observable<T>
}

export abstract class RetrievableApi<T> extends BaseApiV2<T> {
  retrieve (code: string | number, params?: any) {
    return this.http.get<T>(`${environment.apiRoot}/api/${this.baseUrl}/${code}/`, { params })
  }
}

export function isRetrievableApi (object: any): object is RetrievableApi<any> {
  return 'retrieve' in object && typeof object.retrieve === 'function'
}

export interface UpdatableApi<T> {
  update (t: T): Observable<T>
}

export abstract class UpdatableApi<T> extends BaseApiV2<T> {
  update (t: T) {
    return this.http.patch<T>(`${environment.apiRoot}/api/${this.baseUrl}/${t[this.codeField]}/`, t)
  }
}

export function isUpdatableApi (object: any): object is UpdatableApi<any> {
  return 'update' in object && typeof object.update === 'function'
}

export interface DeletableApi<T> {
  delete (t: T): Observable<void>
}

export abstract class DeletableApi<T> extends BaseApiV2<T> {
  delete (t: T): Observable<void> {
    return this.http.delete<void>(`${environment.apiRoot}/api/${this.baseUrl}/${t[this.codeField]}/`)
  }
}

export function isDeletableApi (object: any): object is DeletableApi<any> {
  return 'delete' in object && typeof object.delete === 'function'
}

export interface ImportApi<T> extends BaseApiV2<T> {
  import (theFile: File, data: Record<string, string>, url?: string): Observable<LongRunningJob>
}

export abstract class ImportApi<T> implements ImportApi<T> {
  public import (theFile: File, data: Record<string, string> = {}, url?: string): Observable<LongRunningJob> {
    if (!url) url = 'import'
    const formData: FormData = new FormData()
    formData.append('theFile', theFile, theFile.name)
    for (const key in data) {
      formData.append(key, data[key])
    }
    return this.http.post<LongRunningJob>(`${environment.apiRoot}/api/${this.baseUrl}/${url}/`, formData)
  }
}

export interface ExportApi<T> extends BaseApiV2<T> {
  export (params: Params): Observable<LongRunningJob>
}

export abstract class ExportApi<T> extends BaseApiV2<T> {
  public export (filters: Params): Observable<LongRunningJob> {
    return this.http.post<LongRunningJob>(`${environment.apiRoot}/api/${this.baseUrl}/export/`, { filters: filters })
  }
}

export interface SearchApi<T> extends BaseApiV2<T> {
  search (query: string): Observable<PagedResponse<T>>
}

export abstract class SearchApi<T> implements SearchApi<T> {
  public search (query: string): Observable<PagedResponse<T>> {
    return this.http.get<PagedResponse<T>>(`${environment.apiRoot}/api/${this.baseUrl}/search/?query=${query}`)
  }
}

/**
 * set (persist) the list of visible columns for this table to localStorage
 */
export function setVisibleColumns (tableUniqueKey: string, visibleFields: string[]) {
  if (!tableUniqueKey) return

  const existingPayload = localStorage.getItem('coreColumns')
  let existingData: { [columnName: string]: string[] } = {}
  if (existingPayload) {
    try {
      existingData = JSON.parse(existingPayload)
    } catch (e: any) {
      console.warn('Error parsing coreColumns from localStorage', e.message)
      localStorage.removeItem('coreColumns')
    }
  }
  existingData[tableUniqueKey] = visibleFields
  localStorage.setItem('coreColumns', JSON.stringify(existingData))
}

/**
 * retrieve the list of visible columns for this table from localStorage
 */
export function getVisibleColumns (tableUniqueKey: string): string[] {
  if (!tableUniqueKey) return []

  const existingPayload = localStorage.getItem('coreColumns')
  let existingData: { [columnName: string]: string[] } = {}
  if (existingPayload) {
    try {
      existingData = JSON.parse(existingPayload)
    } catch (e: any) {
      console.warn('Error parsing coreColumns from localStorage', e.message)
      localStorage.removeItem('coreColumns')
    }
  }
  return existingData[tableUniqueKey] || []
}

/**
 * Function used in templates to cast the parameter.
 * @param item
 * @returns item
 */
export function identity<T> (item: T): T {
  return item
}

/**
 * Function throwing an error when called.
 * It takes a parameter of type `never`, typical usage is in the `default` case
 * when switching over an enumeration:
 * ```
 * switch (myEnum) {
 *   case EnumType.A: return true
 *   case EnumType.B: return false
 *   default: return assertUnreachable(myEnum)
 * }
 * ```
 * The purpose being to be sure to get a compilation error when a new value
 * is added to the enum.
 */
export function assertUnreachable (value: never): never {
  throw new Error(`Unexpected call with value: ${value}`)
}

/**
 * Function returning an array containing the textual keys of the given enum.
 * This is necessary as `Object.keys(theEnum)` will return numerical keys as well as textual ones.
 */
export function enumKeys<E extends object> (theEnum: E): Array<keyof E> {
  return Object
    .keys(theEnum)
    .map(key => key as keyof E)
    .filter(key => Number.isNaN(Number(key)))
}

/**
 * Type guard function validating that a given object is not `undefined`.
 * Can be used to filter arrays with proper type check, like:
 * ```
 * const numberArray: number[] = [42, undefined].filter(isDefined)
 * ```
 */
export function isDefined<T> (item: T | undefined): item is T {
  return item !== undefined
}

/**
 * Type guard function validating that a given object is not `null`.
 * Can be used to filter arrays with proper type check, like:
 * ```
 * const numberArray: number[] = [42, null].filter(isNotNull)
 * ```
 */
export function isNotNull<T> (item: T | undefined): item is T {
  return item !== null
}

/**
 * Convenient function to make sure an argument is an Array.
 * Returns an array when an array is given, otherwise returns an array with the given value.
 */
export function asArray<T> (value: T | T[] | undefined): T[] {
  if (value === undefined || value === null) return []
  return Array.isArray(value) ? value : [value]
}

/**
 * Convenient function to use in conjunction with @Memoize
 * in order to take all arguments into account.
 */
export function useAllArgs (...args: any[]) {
  return JSON.stringify(args)
}
